<template>
  <div class="control-rule-builder">
    <div class="rule-joiner">
      <label>Show this field when</label>
      <select v-model="localRules.joiner" class="form-control form-control-sm" style="width:90px" title="ALL = every condition must match (AND) · ANY = at least one matches (OR)">
        <option value="AND">ALL</option>
        <option value="OR">ANY</option>
      </select>
      <span class="rule-joiner-suffix">of these conditions are met:</span>
    </div>

    <div v-for="(cond, idx) in localRules.conditions" :key="idx" class="rule-row">
      <div style="width:220px">
        <div v-if="idx === 0 && totalAvailableCount === 0" style="color:#c00;font-size:12px">No fields available for conditions.</div>
        <select v-model="cond.field" class="form-control form-control-sm" style="width:100%">
          <template v-if="availableFieldsComputed && availableFieldsComputed.length && availableFieldsComputed[0].fields">
            <!-- Render section headers as disabled options and list their fields underneath (avoids optgroup rendering issues) -->
            <template v-for="(g, gi) in availableFieldsComputed">
              <option disabled :key="'h-'+gi" style="font-weight:700">{{ g.sectionName }}</option>
              <option v-for="f in g.fields" :key="'g-'+gi+'-'+f.name" :value="f.name">&nbsp;&nbsp;&nbsp;{{ f.label || f.name }}</option>
            </template>
          </template>
          <template v-else>
            <option v-for="f in availableFieldsComputed" :key="f.name" :value="f.name">{{ f.label || f.name }}</option>
          </template>
        </select>
      </div>

      <!-- For date fields: compare the raw date OR the calculated age (years).
           Use :value + @change with $set so a freshly-added `valueType` is reactive (Vue 2 can't
           track a brand-new property assigned via v-model). -->
      <select v-if="fieldType(cond.field) === 'date'" :value="cond.valueType || 'raw'" @change="setValueType(cond, $event.target.value)" class="form-control form-control-sm" style="width:120px" title="Compare the date value, or the patient's calculated age in years">
        <option value="raw">Date value</option>
        <option value="age">Age (yrs)</option>
      </select>

      <select v-model="cond.op" class="form-control form-control-sm" style="width:130px">
        <option value="equals">equals</option>
        <option value="notEquals">not equals</option>
        <option value="in">in</option>
        <option value="notIn">not in</option>
        <option value="lt">&lt;</option>
        <option value="lte">&lt;=</option>
        <option value="gt">&gt;</option>
        <option value="gte">&gt;=</option>
      </select>

      <input v-model="cond.value" class="form-control form-control-sm" style="width:140px" :placeholder="(fieldType(cond.field) === 'date' && cond.valueType === 'age') ? 'years' : ''" />

      <button class="btn btn-sm btn-danger" style="min-width:70px" @click.prevent="removeCondition(idx)">Remove</button>
    </div>

    <div class="rule-actions">
      <button class="btn btn-sm btn-primary" style="min-width:120px" @click.prevent="addCondition">Add Condition</button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ControlRuleBuilder',
  props: {
    // Backwards-compatible: accept either `rules` + `availableFields` or legacy `control` + `formData`
    rules: { type: Object, default: null },
    availableFields: { type: Array, default: () => null },
    control: { type: Object, default: null },
    formData: { type: Object, default: null }
  },
  data() {
    return {
      localRules: this._cloneRules(this._initialRules())
    }
  },
  computed: {
    availableFieldsComputed() {
      // If consumer passed grouped sections (preferred): [{ sectionName, fields:[{name,label}] }]
      if (Array.isArray(this.availableFields) && this.availableFields.length) {
        const first = this.availableFields[0]
        if (first && first.fields && Array.isArray(first.fields)) {
          const total = this.availableFields.reduce((acc, g) => acc + (g.fields ? g.fields.length : 0), 0)
          if (total > 0) return this.availableFields
          // empty grouped structure provided — fall through to scan formData for flat list
        } else {
          return this.availableFields
        }
      }

      // fall back to scanning formData (legacy) and return grouped structure
      try {
          const fd = this.formData || {}
          const groups = []
          if (fd.sections && (Array.isArray(fd.sections) || typeof fd.sections === 'object')) {
            const sectionsArr = Array.isArray(fd.sections) ? fd.sections : Object.values(fd.sections)
            sectionsArr.forEach((s, si) => {
              const group = { sectionName: s.headline || s.uniqueId || `Section ${si+1}`, fields: [] }
              const rowsArr = Array.isArray(s.rows) ? s.rows : (s.rows && typeof s.rows === 'object' ? Object.values(s.rows) : [])
              rowsArr.forEach(r => {
                  const controlsArr = Array.isArray(r.controls) ? r.controls : (r.controls && typeof r.controls === 'object' ? Object.values(r.controls) : [])
                  controlsArr.forEach(c => {
                    group.fields.push({ name: c.name || c.uniqueid || c.uniqueId, label: c.label || c.name, type: c.type })
                  })
              })
              if (group.fields.length) groups.push(group)
            })
          }
          // if no sections found, try controls map
          if (!groups.length && fd.controls && typeof fd.controls === 'object') {
            const flat = Object.values(fd.controls).map(c => ({ name: c.name || c.uniqueId || c.uniqueid, label: c.label || c.name, type: c.type }))
            if (flat.length) return flat
          }
          if (groups.length) return groups
          return []
        } catch (e) {
          return []
        }
    }
    ,
    totalAvailableCount() {
      const af = this.availableFieldsComputed || []
      if (!af.length) return 0
      if (af[0] && af[0].fields && Array.isArray(af[0].fields)) {
        return af.reduce((acc, g) => acc + (g.fields ? g.fields.length : 0), 0)
      }
      return af.length
    }
  },
  mounted() {
    // debug: log available fields shape when builder mounts
    try { console.debug('ControlRuleBuilder availableFieldsComputed:', this.availableFieldsComputed) } catch (e) { /* ignore logging errors */ }
  },
  watch: {
    rules: { handler(n) { if (n) this.localRules = this._cloneRules(n) }, deep: true },
    control: { handler() { const r = this._initialRules(); if (r) this.localRules = this._cloneRules(r) }, deep: true },
    localRules: { handler() { this.$emit('update:rules', this._cloneRules(this.localRules)) }, deep: true }
  },
  methods: {
    _initialRules() {
      if (this.rules) return this.rules
      if (this.control && this.control.visibilityRules && Array.isArray(this.control.visibilityRules) && this.control.visibilityRules.length) return this.control.visibilityRules[0]
      return { joiner: 'OR', conditions: [] }
    },
    addCondition() {
      let firstField = ''
      const af = this.availableFieldsComputed || []
      if (af.length) {
        // grouped format
        if (af[0] && af[0].fields && Array.isArray(af[0].fields)) {
          const grp = af.find(g => g.fields && g.fields.length) || af[0]
          firstField = (grp && grp.fields && grp.fields[0] && grp.fields[0].name) || ''
        } else {
          firstField = (af[0] && af[0].name) || ''
        }
      }
      this.localRules.conditions.push({ field: firstField, op: 'equals', value: '' })
    },
    removeCondition(i) { this.localRules.conditions.splice(i, 1) },
    /**
     * Set a condition's compare-as mode reactively ($set so a new `valueType` key is tracked,
     * which lets the deep `localRules` watcher emit update:rules and persist the change).
     */
    setValueType(cond, val) {
      this.$set(cond, 'valueType', val)
    },
    /**
     * Look up the control type for a given field name from the available-fields list.
     * Used to reveal the "Compare as: Date / Age" selector only for date fields.
     */
    fieldType(fieldName) {
      if (!fieldName) return ''
      const af = this.availableFieldsComputed || []
      if (!af.length) return ''
      // grouped: [{ sectionName, fields: [{ name, label, type }] }]
      if (af[0] && af[0].fields && Array.isArray(af[0].fields)) {
        for (const g of af) {
          const f = (g.fields || []).find(x => x.name === fieldName)
          if (f) return f.type || ''
        }
        return ''
      }
      // flat: [{ name, label, type }]
      const f = af.find(x => x.name === fieldName)
      return (f && f.type) || ''
    },
    _cloneRules(r) { try { return JSON.parse(JSON.stringify(r || { joiner: 'OR', conditions: [] })) } catch (e) { return { joiner: 'OR', conditions: [] } } }
  }
}
</script>

<style scoped>
.condition-row select, .condition-row input { min-width: 0 }
.rule-row { display:flex; gap:8px; margin-bottom:8px }
.rule-actions { margin-top:8px }
.rule-joiner { margin-bottom:8px; display:flex; gap:8px; align-items:center }
.rule-joiner-suffix { color:#6b7280; font-size:13px }
</style>
